"use server";
import { redirect } from "next/navigation";
import { login as doLogin, loginWithTotp, logout as doLogout, getSessionUser, myCompanies, prepareUserPasswordReset, resetUserPassword } from "@/lib/auth";
import { sendMail, mailShell } from "@/lib/mailer";
import { newCaptcha, verifyCaptcha } from "@/lib/captcha";
import { rateLimit } from "@/lib/rate-limit";
import { clientIp } from "@/lib/request-info";
import { destinatieSigura, ePlecareCompleta } from "@/lib/destinatie-login";

/** Un CAPTCHA proaspăt pentru formularele publice. Limitat pe IP, ca generarea să nu fie ea însăși o gaură. */
export async function newCaptchaAction(): Promise<{ token: string; image: string; code?: string } | { error: string }> {
  if (!rateLimit(`captcha:ip:${await clientIp()}`, 120, 15 * 60 * 1000)) return { error: "Prea multe încercări. Așteaptă câteva minute." };
  return newCaptcha();
}

const CAPTCHA_GRESIT = "Codul din imagine nu e corect. Uită-te la imaginea nouă și scrie-l din nou.";

/** Pasul întâi. `challenge` întors = parola a fost bună, dar contul cere codul din aplicație. */
/** După autentificare: pagina cerută inițial (`next`, validată) sau „/”. O rută API (plecarea SSO
 *  spre eDocs) se întoarce ca adresă — formularul face navigarea completă; restul rămân pe redirect. */
function laDestinatie(next?: string): { goTo: string } {
  const dest = destinatieSigura(next) ?? "/";
  if (ePlecareCompleta(dest)) return { goTo: dest };
  redirect(dest);
}

export async function loginAction(email: string, password: string, captcha: { token: string; code: string }, next?: string): Promise<{ error?: string; challenge?: string; goTo?: string }> {
  /* Motivul eșecului merge în journal (fără parole și fără codul din imagine) — altfel un
     login care pică la un om real e invizibil: rezultatul se ÎNTOARCE ca date, nu se aruncă. */
  const ip = await clientIp();
  if (!verifyCaptcha(captcha?.token, captcha?.code)) {
    console.log(`[login] esec captcha ip=${ip} email=${email.trim().toLowerCase()}`);
    return { error: CAPTCHA_GRESIT };
  }
  const r = await doLogin(email, password);
  if (r.status === "totp") return { challenge: r.challenge };
  if (r.status === "schimba") redirect(`/parola/${encodeURIComponent(r.token)}?temporara=1`);
  if (r.status === "error") {
    console.log(`[login] esec credentiale ip=${ip} email=${email.trim().toLowerCase()}`);
    return { error: r.error };
  }
  return laDestinatie(next);
}

/** Pasul doi: codul TOTP sau un cod de recuperare. */
export async function loginTotpAction(challenge: string, code: string, next?: string): Promise<{ error?: string; goTo?: string }> {
  const r = await loginWithTotp(challenge, code);
  if (!r.ok) return { error: r.error };
  return laDestinatie(next);
}

/**
 * Cerere de resetare. Răspunsul e ACELAȘI indiferent dacă adresa are cont sau nu —
 * altfel formularul ar spune oricui care angajați au cont la noi.
 */
const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

export async function requestPasswordResetAction(email: string, captcha: { token: string; code: string }): Promise<{ ok: true } | { error: string }> {
  if (!verifyCaptcha(captcha?.token, captcha?.code)) return { error: CAPTCHA_GRESIT };
  const prep = await prepareUserPasswordReset(email);
  if (prep) {
    const base = process.env.RODESK_AI_URL || "https://app.rodesk.ai";
    const link = `${base}/parola/${encodeURIComponent(prep.token)}`;
    /* NU se așteaptă trimiterea. Un `await` aici ar face ca adresele CU cont să răspundă
       după un dus-întors SMTP, iar cele fără cont instant — adică exact enumerarea pe care
       mesajul identic o evită. `sendMail` își înghite singur erorile, deci nu rămâne
       nicio respingere neprinsă. */
    void sendMail({
      to: prep.email,
      subject: "Resetare parolă Rodesk.ai",
      html: mailShell("Resetare parolă", `
        <p style="font-size:14px">Salut, ${escapeHtml(prep.name)}.</p>
        <p style="font-size:14px">Ai cerut resetarea parolei. Linkul e valabil o oră:</p>
        <p><a href="${link}" style="display:inline-block;background:#1a1a1a;color:#fff;padding:10px 18px;border-radius:8px;text-decoration:none;font-size:14px">Setează parola nouă</a></p>
        <p style="font-size:12px;color:#666">Dacă nu ai cerut tu resetarea, ignoră mesajul — parola rămâne neschimbată.</p>`,
        "Rodesk.ai"),
    }).catch(() => {});
  }
  return { ok: true };
}

export async function resetPasswordAction(token: string, newPassword: string): Promise<{ error?: string }> {
  const r = await resetUserPassword(token, newPassword);
  if (!r.ok) return { error: r.error };
  redirect("/login?resetat=1");
}

export async function logoutAction(): Promise<void> {
  await doLogout();
  redirect("/login");
}

export async function whoami() {
  return getSessionUser();
}

/**
 * Firmele proprii + care e cea activă — pentru comutatorul din bara laterală.
 *
 * Se cer PRINTR-O ACȚIUNE, nu prin lărgirea lui `whoami()`: `whoami` ajunge la client pe
 * fiecare pagină, iar lista firmelor n-are ce căuta acolo când omul are una singură.
 */
export async function myCompaniesAction(): Promise<{
  companies: { companyId: string; name: string; role: string; roleName: string; groupName: string | null; viaGroup: boolean; testData: boolean }[];
  activeCompanyId: string;
} | null> {
  const me = await getSessionUser();
  if (!me) return null;
  return { companies: await myCompanies(me.id), activeCompanyId: me.companyId };
}
